import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEventWithUTM } from '../../utils/analytics';
import OptimizedImage from '../OptimizedImage';
import { PRICING_CACHE_PREFIX } from '../../services/pricingService';
import './KashiPujas.css';

// ---------------------------------------------------------------------------
// Read the PLATFORM_SITE pricing cache (written by CityPricingContext on every
// page load) and return the GST-inclusive price for a given service name.
// Falls back to the hardcoded price when the cache is absent or the service is
// not found — guarantees the cards are never empty.
// ---------------------------------------------------------------------------
const getPlatformSitePrice = (pujaName: string, fallback: string): string => {
  try {
    const raw = localStorage.getItem(`${PRICING_CACHE_PREFIX}PLATFORM_SITE`);
    if (!raw) return fallback;
    const cached = JSON.parse(raw);
    if (!cached?.services?.length) return fallback;

    const service = cached.services.find((s: any) => {
      const sName = (s.serviceName || '').toLowerCase();
      const pName = pujaName.toLowerCase();
      return sName === pName || sName.includes(pName) || pName.includes(sName);
    });

    if (service?.plans?.length > 0) {
      const total = Math.round(service.plans[0].totalAmount);
      return `₹${total.toLocaleString('en-IN')}`;
    }
  } catch { /* ignore parse errors */ }
  return fallback;
};

// ─────────────────────────────────────────────────────────────────────────────
// ██  CONFIGURATION  ██████████████████████████████████████████████████████████
//
//  • To HIDE a card:     set  enabled: false
//  • To REORDER cards:   change the array order
//  • To UPDATE details:  edit any field inline — no JSX changes needed
//  • Max recommended:    6 cards (2 rows × 3 col)
//
// ─────────────────────────────────────────────────────────────────────────────

interface KashiPuja {
  id: string;
  name: string;
  image: string;
  price: string;
  whyKashi: string;           // "Why perform in Kashi?" one-liner
  bookingPath: string;
  badge?: string;             // optional top-left badge e.g. "Most Booked"
  locationLabel?: string;     // bottom-left overlay e.g. "Kashi Ghats"
  enabled: boolean;
}

// ─── KASHI PUJA DATA ─────────────────────────────────────────────────────────
const KASHI_PUJAS: KashiPuja[] = [
  {
    id: 'kashi-shradh',
    name: 'Kashi Shradh Karm',
    image: '/images/shradh_karm/kashi.jpg',
    price: '₹55,000',
    whyKashi: 'Kashi is the holiest site for ancestral liberation — moksha is assured here.',
    bookingPath: '/booking/shradh-karm/kashi-shradh-karm',
    badge: 'Most Sacred',
    locationLabel: '📍 Manikarnika Ghat, Kashi',
    enabled: true,
  },
  {
    id: 'shani-shanti',
    name: 'Shani Shanti Pujan',
    image: '/images/navgrah/shani.jpg',
    price: '₹21,000',
    whyKashi: "Performed at our Antaryatra site in Kashi — amplifies the ritual's potency manifold.",
    bookingPath: 'booking/navagrah-pujan/shani-shanti-pujan',
    locationLabel: '📍 Antaryatra Site, Kashi',
    enabled: true,
  },
  {
    id: 'rudrabhishekh',
    name: 'Rudrabhishekh at Kashi',
    image: '/images/rudrabhishek.jpg',
    price: '₹21,500',
    whyKashi: 'Lord Shiva resides eternally in Kashi — Rudrabhishekh performed here by our Acharyas carries infinite potency.',
    bookingPath: '/booking/shiv-pujan/rudrabhishekh',
    badge: 'Most Powerful',
    locationLabel: '📍 Antaryatra Site, Kashi',
    enabled: true,
  },
  {
    id: 'pitra-dosh',
    name: 'Pitra Dosh Nivaran',
    image: '/images/dosh_nivaran/pitra_dosh.jpg',
    price: '₹45,000',
    whyKashi: "Kashi's sacred energy dissolves ancestral karma faster than any other location.",
    bookingPath: '/booking/dosh-nivaran/pitra-dosh-nivaran',
    locationLabel: '📍 Antaryatra Site, Kashi',
    enabled: true,
  },
  {
    id: 'tripindi-shradh',
    name: 'Tripindi Shradh',
    image: '/images/shradh_karm/tripindi.jpg',
    price: '₹65,000',
    whyKashi: "Three generations of unsettled souls find the swiftest peace in Kashi's divine field.",
    bookingPath: '/booking/shradh-karm/tripindi-shradh',
    locationLabel: '📍 Antaryatra Site, Kashi',
    enabled: true,
  },
  {
    id: 'kaal-sarp',
    name: 'Kaal Sarp Dosh Nivaran',
    image: '/images/dosh_nivaran/kaal_sarp.jpg',
    price: '₹35,000',
    whyKashi: 'The Puranas prescribe Kashi for Kaal Sarp remedies — performed at our Antaryatra site with the complete Vedic process.',
    bookingPath: '/booking/dosh-nivaran/kaal-sarp-dosh-nivaran',
    locationLabel: '📍 Antaryatra Site, Kashi',
    enabled: true,
  },
];

// ─── TRUST ITEMS ─────────────────────────────────────────────────────────────
const TRUST_ITEMS = [
  { icon: '🌍', text: 'Book from anywhere in the world' },
  { icon: '📹', text: 'Video of your pooja ritual' },
  { icon: '📜', text: 'Prasadam delivered to your home any where' },
  { icon: '🙏', text: 'Kashi-lineage Acharyas only' },
];

// ─────────────────────────────────────────────────────────────────────────────
// ██  END CONFIGURATION  ██████████████████████████████████████████████████████
// ─────────────────────────────────────────────────────────────────────────────

const activePujas = KASHI_PUJAS.filter((p) => p.enabled);

const KashiPujas: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  // Live prices from PLATFORM_SITE cache — keyed by puja id, fall back to hardcoded price
  const [livePrices, setLivePrices] = useState<Record<string, string>>({});

  useEffect(() => {
    const prices: Record<string, string> = {};
    activePujas.forEach(puja => {
      prices[puja.id] = getPlatformSitePrice(puja.name, puja.price);
    });
    setLivePrices(prices);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEventWithUTM('homepage_section_viewed', 'homepage', 'kashi_pujas');
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="kashi-pujas" id="kashi-pujas">
      {/* ── Header ──────────────────────────────────────────────── */}
      <p className="kashi-pujas__eyebrow">
        <span className="kashi-pujas__eyebrow-icon" aria-hidden="true"></span>
        Sacred Rituals in Kashi
      </p>
      <h2 className="kashi-pujas__title">
        Sacred Rituals, Best Performed in{' '}
        <span className="kashi-pujas__title-accent">Kashi</span>
      </h2>
      <p className="kashi-pujas__subtitle">
        Some rituals carry infinitely greater power when performed in Kashi - the city where Lord Shiva himself resides. Our Acharyas perform these on your behalf.
      </p>

      {/* ── NRI badge ────────────────────────────────────────────── */}
      <div className="kashi-pujas__nri-badge">
        <span className="kashi-pujas__nri-badge-flag" aria-hidden="true">🌍</span>
        Perfect for NRIs &amp; families who cannot travel - we perform personalized rituals for you (Option to attend in person at our site if you wish)
      </div>

      {/* ── Grid ─────────────────────────────────────────────────── */}
      <div className="kashi-pujas__grid">
        {activePujas.map((puja) => (
          <article className="kashi-card" key={puja.id}>
            {/* Image */}
            <div className="kashi-card__image-wrap">
              <OptimizedImage
                src={puja.image}
                alt={puja.name}
                className="kashi-card__image"
              />
              {puja.badge && (
                <span className="kashi-card__badge">{puja.badge}</span>
              )}
              {puja.locationLabel && (
                <span className="kashi-card__location-tag">{puja.locationLabel}</span>
              )}
            </div>

            {/* Body */}
            <div className="kashi-card__body">
              <h3 className="kashi-card__name">{puja.name}</h3>
              <p className="kashi-card__why">
                <strong>Why Kashi:</strong> {puja.whyKashi}
              </p>
              <div className="kashi-card__footer">
                <span className="kashi-card__price">{livePrices[puja.id] ?? puja.price}</span>
                <Link
                  to={puja.bookingPath}
                  state={{ preferPlatformSite: true }}
                  className="kashi-card__book-btn"
                  onClick={() =>
                    trackEventWithUTM('homepage_kashi_puja_click', 'kashi_pujas', puja.name, {
                      puja_id: puja.id,
                      price: livePrices[puja.id] ?? puja.price,
                      location_label: puja.locationLabel,
                    })
                  }
                >
                  Book Now →
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* ── Trust strip ──────────────────────────────────────────── */}
      <div className="kashi-pujas__trust">
        {TRUST_ITEMS.map((item) => (
          <div className="kashi-pujas__trust-item" key={item.text}>
            <span className="kashi-pujas__trust-icon" aria-hidden="true">{item.icon}</span>
            {item.text}
          </div>
        ))}
      </div>
    </section>
  );
};

export default KashiPujas;
